import { c as _c } from "react/compiler-runtime";
// Э7: сводка верификации продавца (fd-onboard-12). Показывает чек-лист
// обязательных документов «личного дела» в трёх состояниях (нет / на проверке /
// отклонён / принят), результат авто-проверки ИНН и итоговый «зелёный флаг»
// «Проверенный продавец», когда документы приняты, ИНН проверен и продавец
// активен. Данные — GET /admin/sellers/:userId/verification (гард page.shops).
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BadgeCheck, Check, CircleDashed, Clock, Undo2, X } from "lucide-react";
import type { SellerApplication, SellerDocumentChecklistStatus, SellerDocumentType } from "@/api";
import { REWORKABLE_STATUSES } from "@/api/hooks/use-applications";
import { useSellerVerification } from "@/api/hooks/use-sellers";
import { ReworkDialog } from "@/components/application-rework";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { cn } from "@/lib/utils";
const STATUS_ICON: Record<SellerDocumentChecklistStatus, typeof Check> = {
  accepted: Check,
  uploaded: Clock,
  rejected: X,
  missing: CircleDashed
};
const STATUS_STYLE: Record<SellerDocumentChecklistStatus, string> = {
  accepted: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-500",
  uploaded: "bg-amber-500/15 text-amber-600 dark:text-amber-500",
  rejected: "bg-destructive/15 text-destructive",
  missing: "border border-dashed text-muted-foreground"
};
export function SellerVerificationPanel(t0) {
  const $ = _c(33);
  const {
    userId,
    application
  } = t0;
  const {
    t
  } = useTranslation();
  const query = useSellerVerification(userId);
  const verification = query.data;
  const [reworkDocType, setReworkDocType] = useState(null);
  let t1;
  if ($[0] !== application) {
    t1 = !!application && REWORKABLE_STATUSES.includes(application.status);
    $[0] = application;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canRework = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("sellerVerification.title");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <CardTitle className="text-base">{t2}</CardTitle>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t || $[7] !== verification?.verified) {
    t4 = verification?.verified ? <Badge className="gap-1 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-400"><BadgeCheck className="size-3.5" />{t("sellerVerification.verified")}</Badge> : <Badge variant="outline" className="text-muted-foreground">{t("sellerVerification.notVerified")}</Badge>;
    $[6] = t;
    $[7] = verification?.verified;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">{t3}{t4}</CardHeader>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== query.isPending || $[13] !== t) {
    t6 = query.isPending && <p className="text-muted-foreground">{t("sellerVerification.loading")}</p>;
    $[12] = query.isPending;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== query.error) {
    t7 = query.error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">{query.error.message}</div>;
    $[15] = query.error;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== canRework || $[18] !== t || $[19] !== verification) {
    t8 = verification && <><ul className="flex flex-col gap-2">{verification.checklist.map(item => {
          const Icon = STATUS_ICON[item.status];
          return <li key={item.docType} className="flex items-center gap-3"><span className={cn("flex size-5 shrink-0 items-center justify-center rounded-full", STATUS_STYLE[item.status])}><Icon className="size-3.5" /></span><span className="min-w-0 flex-1">{t(`sellers.personalFile.docType.${item.docType}`)}</span><span className="shrink-0 text-xs text-muted-foreground">{t(`sellerVerification.status.${item.status}`)}</span>{canRework && <Button size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs" onClick={() => setReworkDocType(item.docType)}><Undo2 className="size-3.5" />{t("applications.rework.sectionAction")}</Button>}</li>;
        })}</ul>{verification.inn && <div className="flex items-center justify-between gap-3 border-t pt-3"><div className="min-w-0"><p className="text-xs uppercase tracking-wider text-muted-foreground">{t("sellerVerification.inn")}</p><p className="tabular-nums">{verification.inn.inn}</p>{verification.inn.name && <p className="truncate text-xs text-muted-foreground">{verification.inn.name}</p>}{verification.inn.okved && <p className="truncate text-xs text-muted-foreground">{t("sellerVerification.okved", {
              defaultValue: "\u041E\u041A\u0412\u042D\u0414"
            })}{" "}{verification.inn.okved}{verification.inn.okvedName ? ` · ${verification.inn.okvedName}` : ""}</p>}</div><Badge variant={verification.inn.status === "verified" ? "secondary" : "outline"} className={cn(verification.inn.status === "verified" && "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-400", verification.inn.status === "not_found" && "text-destructive")}>{t(`sellerVerification.innStatus.${verification.inn.status}`)}</Badge></div>}{verification.inn?.warnings?.map(_temp)}</>;
    $[17] = canRework;
    $[18] = t;
    $[19] = verification;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <CardContent className="flex flex-col gap-4 text-sm">{t6}{t7}{t8}</CardContent>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== application || $[26] !== reworkDocType || $[27] !== verification?.checklist) {
    t10 = application && reworkDocType && <ReworkDialog open={true} applicationId={application.id} shopName={application.shopName} docType={reworkDocType} allowedDocTypes={verification?.checklist.map(_temp2)} onClose={() => setReworkDocType(null)} />;
    $[25] = application;
    $[26] = reworkDocType;
    $[27] = verification?.checklist;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t5 || $[31] !== t9) {
    t11 = <Card>{t5}{t9}{t10}</Card>;
    $[29] = t10;
    $[30] = t5;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function _temp2(item_0) {
  return item_0.docType;
}
function _temp(warning) {
  return <p key={warning} className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300">{warning}</p>;
}